/**
 * The second section library — **thirteen** types C21 adds (FR-I19,
 * [ADR-0043](../../../../docs/adr/0043-thirteen-themes-in-families-and-a-second-section-library.md)
 * §2), a reader and a **shared** renderer each, in the shape `library.tsx` set
 * for FR-I14's thirteen and `events.tsx` kept for C17's three.
 *
 * Generic: `pricing` · `tabs` · `timeline` · `video` · `before_after` ·
 * `marquee` · `comparison` · `hours`.
 * Vertical, display only: `price_list` · `courses` · `openings` · `goal` ·
 * `product_grid`.
 *
 * ## Shared, and real, on all nine existing themes
 *
 * ADR-0043 §4 is the second bounded exception to ADR-0026 §1, on ADR-0036 §3's
 * precedent: the nine themes that exist draw these thirteen through the
 * renderers below, and each new theme redraws the eight generic ones and its
 * own family's verticals. So these are designs, not placeholders — laid out in
 * `templates/base.ts: LIBRARY_TWO_CSS` on the palette's own custom properties,
 * so a firm's colours and each theme's paper, ink and rules reach them unasked.
 * That stylesheet is sent **only to a page that carries one of these**
 * (`usesLibraryTwo`), so no page drawn before C21 changes by a byte.
 *
 * ## The reader holds the rules
 *
 * As in `library.tsx`: `readX(props, ctx)` is the only place a rule is applied
 * — the price policy, the refusal of a hostile link, the emptiness test — and
 * the view-model (`site/types.ts`, waves.md Wave 21 I-1) is all a renderer
 * sees. **Every price is read twice**: the service drops it from the payload
 * where `show_prices_public` is off (21A), and the reader nulls it again, so a
 * payload built from a fixture, a sample or a future caller cannot draw one.
 *
 * ## No script, ever — ADR-0013 §2, ADR-0043 §5
 *
 * - **Tabs** (and `showcase`'s filter chips, in `library.tsx`) are radio inputs
 *   and labels; CSS shows the checked panel through `:has()`. With no CSS, or
 *   where `:has()` is missing, or on a phone, every panel is a stacked section
 *   under its own heading — the first panel is always shown.
 * - **Before and after** is `resize: horizontal` on the upper picture's frame
 *   on a pointer device, and the two pictures side by side on touch.
 * - **The marquee** moves a duplicated, `aria-hidden` copy with a `from`-only
 *   keyframe; it pauses on hover and focus and is still under reduced motion.
 * - **The video** is a poster that links out. There is no `<iframe>`, no
 *   player and no third party's script on a firm's page (§9-72).
 *
 * ## Nothing claimed that the firm did not type
 *
 * Every word and figure is the firm's, or the catalog's, or *Your firm*'s
 * hours. The words this file writes are structure, never a claim: a closed
 * day reads *Closed*, the showcase's first chip reads *All*, and the two
 * pictures of a pair are told apart as *Before* and *After*. There are no
 * ticks, crosses, stars or icons (ADR-0022, ADR-0043 §3); the only drawing is
 * the video's play mark, `aria-hidden`, which says what the link does.
 */

import type { CSSProperties, ReactNode } from 'react'

import { href as safeHref, list, obj, rupees, src, str } from '../read'
import type {
  BeforeAfterView,
  ComparisonView,
  CourseCard,
  CoursesView,
  GoalView,
  HoursDay,
  HoursView,
  MarqueeView,
  OpeningRole,
  OpeningsView,
  PriceListItem,
  PriceListView,
  PricingCard,
  PricingPlan,
  PricingView,
  ProductCard,
  ProductGridView,
  TabItem,
  TabsView,
  TimelineView,
  VideoView,
} from '../types'
import { Buttons, link, readButtons, Section, type BlockContext } from './common'
import { hasRichText, RichTextBody } from './rich'

export type {
  BeforeAfterView,
  ComparisonView,
  CourseCard,
  CoursesView,
  GoalView,
  HoursDay,
  HoursView,
  MarqueeView,
  OpeningRole,
  OpeningsView,
  PriceListItem,
  PriceListView,
  PricingCard,
  PricingPlan,
  PricingView,
  ProductCard,
  ProductGridView,
  TabItem,
  TabsView,
  TimelineView,
  VideoView,
} from '../types'

/**
 * The stagger step a theme writes on a child, **capped at 8** (ADR-0043 §5):
 * `style={stagger(i)}` gives `--i: min(i, 8)`, and the theme's delay is
 * `calc(var(--i) * <step>)`. The cap is what `proveTheme`'s motion check holds
 * — a twelfth card must not wait twelve steps to appear.
 */
export function stagger(index: number): CSSProperties {
  const i = Number.isInteger(index) && index > 0 ? Math.min(index, 8) : 0
  return { '--i': i } as CSSProperties
}

/** The thirteen, in `BLOCK_TYPES` order (types 26–38). */
export const LIBRARY_TWO_TYPES = [
  'pricing',
  'tabs',
  'timeline',
  'video',
  'before_after',
  'marquee',
  'comparison',
  'hours',
  'price_list',
  'courses',
  'openings',
  'goal',
  'product_grid',
] as const

/** ADR-0043 §2's eight generic sections — every new theme redraws these (§4). */
export const GENERIC_SECTIONS = [
  'pricing',
  'tabs',
  'timeline',
  'video',
  'before_after',
  'marquee',
  'comparison',
  'hours',
] as const

/** ADR-0043 §2's five vertical, display-only sections — a theme redraws its own family's. */
export const VERTICAL_SECTIONS = ['price_list', 'courses', 'openings', 'goal', 'product_grid'] as const

/**
 * Whether any page of a payload carries one of the thirteen, or a showcase
 * with its chips switched on — the only pages `LIBRARY_TWO_CSS` is sent to
 * (`render.tsx`), so every page drawn before C21 keeps its stylesheet byte
 * for byte.
 */
export function usesLibraryTwo(pages: readonly { blocks?: readonly { type?: string; props?: unknown }[] | null }[] | null | undefined): boolean {
  const types = new Set<string>(LIBRARY_TWO_TYPES)
  for (const page of pages ?? []) {
    for (const block of page?.blocks ?? []) {
      if (!block) continue
      if (types.has(block.type ?? '')) return true
      if (block.type === 'showcase' && obj(block.props).filterable === true) return true
    }
  }
  return false
}

/** A price the firm typed, or `null` when the policy hides prices. */
function priceText(value: unknown, ctx: BlockContext): string | null {
  return ctx.showPricing ? str(value) : null
}

/** A whole number of at least zero, or `null` — `num()` refuses a zero, which a goal's *raised* may be. */
function count(value: unknown): number | null {
  const raw = typeof value === 'number' ? value : Number(str(value))
  return Number.isInteger(raw) && raw >= 0 ? raw : null
}

/** An address that leaves the site: absolute http(s) only. */
function external(value: unknown): string | null {
  const to = safeHref(value)
  return to && /^https?:\/\//.test(to) ? to : null
}

/* ── pricing ───────────────────────────────────────────────────────────── */

function planPrice(plan: Record<string, unknown>, showPrices: boolean): string | null {
  if (!showPrices) return null
  const price = obj(plan.price)
  if (price.amount !== undefined) return rupees(price.amount)
  return str(plan.price_text) ?? rupees(plan.price)
}

function planPeriod(plan: Record<string, unknown>): string | null {
  const price = obj(plan.price)
  return (str(plan.period) ?? str(price.billing_cadence ?? plan.billing_cadence))?.replace(/_/g, ' ') ?? null
}

/**
 * **The catalog, as the service resolved it** — `product_spotlight`'s rule
 * exactly (ADR-0043 §2): the block stores `service_slugs` and nothing about a
 * service, and every name and fee here came from `prodesk_services` on this
 * render. A slug that no longer resolves has already been dropped; a card with
 * nothing to say is dropped here.
 *
 * Read tolerantly, because the resolved shape is 21A's: a service's `plans`
 * (each `name` or `label`, a `price` object or a `price_text`, a `period` or
 * `billing_cadence`), or — for a catalog row with one headline fee and no named
 * plan — the service's own `price`, drawn as one unnamed plan.
 */
export function readPricing(props: Record<string, unknown>, ctx: BlockContext): PricingView | null {
  const showPrices = ctx.showPricing && props.show_prices !== false
  const highlightSlug = str(props.highlight_slug)
  const badge = str(props.badge)
  const cards = list(props.items ?? props.cards)
    .map((raw): PricingCard | null => {
      const item = obj(raw)
      const name = str(item.name) ?? str(item.title)
      if (!name) return null
      let plans = list(item.plans)
        .map((p): PricingPlan | null => {
          const plan = obj(p)
          const label = str(plan.name) ?? str(plan.label)
          const price = planPrice(plan, showPrices)
          if (!label && !price) return null
          return { label, price, period: price ? planPeriod(plan) : null }
        })
        .filter((p): p is PricingPlan => p !== null)
        .slice(0, 6)
      if (plans.length === 0) {
        const price = planPrice(item, showPrices)
        if (price) plans = [{ label: null, price, period: planPeriod(item) }]
      }
      const highlight = item.highlight === true || (highlightSlug !== null && str(item.slug) === highlightSlug)
      return {
        name,
        summary: str(item.description) ?? str(item.summary),
        plans,
        highlight,
        badge: highlight ? (str(item.badge) ?? badge) : null,
        buttons: readButtons(item.buttons, ctx),
      }
    })
    .filter((c): c is PricingCard => c !== null)
    .slice(0, 4)
  if (cards.length === 0) return null
  // The service puts the section's buttons on every card (21A's resolver) and
  // leaves them on the section too: drawn once, on the cards. A payload whose
  // cards carry none draws the section's under the cards instead.
  const onCards = cards.some((c) => c.buttons.length > 0)
  return {
    heading: str(props.heading),
    intro: str(props.intro),
    cards,
    buttons: onCards ? [] : readButtons(props.buttons, ctx),
  }
}

export function Pricing(view: PricingView): ReactNode {
  const { cards, intro, buttons } = view
  return (
    <Section className={`block pricing pricing-${cards.length}`} heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="pricing-cards">
        {cards.map((card, i) => (
          <li className={`pricing-card${card.highlight ? ' pricing-card-highlight' : ''}`} key={i}>
            {card.badge ? <p className="pricing-badge">{card.badge}</p> : null}
            <h3 className="pricing-name">{card.name}</h3>
            {card.summary ? <p className="pricing-summary">{card.summary}</p> : null}
            {card.plans.length > 0 ? (
              <ul className="pricing-plans">
                {card.plans.map((plan, j) => (
                  <li className="pricing-plan" key={j}>
                    {plan.label ? <span className="pricing-plan-label">{plan.label}</span> : null}
                    {plan.price ? (
                      <span className="pricing-price">
                        {plan.price}
                        {plan.period ? <span className="pricing-period"> {plan.period}</span> : null}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            <Buttons className="pricing-actions" buttons={card.buttons} />
          </li>
        ))}
      </ul>
      <Buttons className="pricing-section-actions" buttons={buttons} />
    </Section>
  )
}

/* ── tabs ──────────────────────────────────────────────────────────────── */

export function readTabs(props: Record<string, unknown>, ctx: BlockContext, index = 0): TabsView | null {
  const tabs = list(props.tabs)
    .map((raw): TabItem | null => {
      const tab = obj(raw)
      const label = str(tab.label)
      if (!label) return null
      const title = str(tab.title)
      const body = hasRichText(tab.body) ? tab.body : null
      const image = src(tab.image_url)
      // A tab with a label and nothing behind it is a control that opens nothing.
      if (!title && !body && !image) return null
      return { label, title, body, image, imageAlt: str(tab.image_alt) }
    })
    .filter((t): t is TabItem => t !== null)
    .slice(0, 6)
  if (tabs.length === 0) return null
  return { heading: str(props.heading), tabs, group: `tabs${index}`, buttons: readButtons(props.buttons, ctx) }
}

/**
 * Tabs on a wide screen, a stack everywhere else — **CSS only**. The radios and
 * their labels sit together in `.tabs-list`, before the panels, so
 * `.tabs-frame:has(.tabs-radio:nth-of-type(n):checked)` can reach both. The
 * first radio is checked in the markup. The radios are real inputs in one
 * group, so a keyboard reaches them with Tab and moves between them with the
 * arrow keys; the label shows the focus ring.
 *
 * One tab draws no tab row at all. Each panel carries its label as a heading,
 * shown whenever the panels stack and hidden (not removed) behind the tab row.
 */
export function Tabs(view: TabsView): ReactNode {
  const { tabs, group, buttons } = view
  const tabbed = tabs.length > 1
  return (
    <Section className={`block tabs${tabbed ? ' tabs-tabbed' : ''}`} heading={view.heading}>
      <div className="tabs-frame">
        {tabbed ? (
          <div className="tabs-list">
            {tabs.map((tab, i) => [
              <input
                key={`r${i}`}
                type="radio"
                className="tabs-radio"
                name={group}
                id={`${group}-t${i}`}
                defaultChecked={i === 0}
              />,
              <label key={`l${i}`} className="tabs-tab" htmlFor={`${group}-t${i}`}>
                {tab.label}
              </label>,
            ])}
          </div>
        ) : null}
        <div className="tabs-panels">
          {tabs.map((tab, i) => (
            <div className={`tabs-panel${tab.image ? ' tabs-panel-image' : ''}`} key={i}>
              <div className="tabs-copy">
                <h3 className="tabs-panel-label">{tab.label}</h3>
                {tab.title ? <p className="tabs-title">{tab.title}</p> : null}
                {tab.body ? (
                  <div className="prose tabs-body">
                    <RichTextBody value={tab.body} />
                  </div>
                ) : null}
              </div>
              {tab.image ? (
                <div className="tabs-art">
                  <img src={tab.image} alt={tab.imageAlt ?? ''} width={640} height={480} loading="lazy" decoding="async" />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <Buttons className="tabs-actions" buttons={buttons} />
    </Section>
  )
}

/* ── timeline ──────────────────────────────────────────────────────────── */

export function readTimeline(props: Record<string, unknown>, ctx: BlockContext): TimelineView | null {
  const entries = list(props.entries)
    .map((raw) => {
      const entry = obj(raw)
      const when = str(entry.when)
      const title = str(entry.title)
      return when && title ? { when, title, body: str(entry.body) } : null
    })
    .filter((e): e is TimelineView['entries'][number] => e !== null)
    .slice(0, 12)
  if (entries.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), entries, buttons: readButtons(props.buttons, ctx) }
}

/** A vertical line with a point per entry. `when` is the firm's text — *"2019"*, *"FY 2024-25"* — never parsed. */
export function Timeline(view: TimelineView): ReactNode {
  const { entries, intro, buttons } = view
  return (
    <Section className="block timeline" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ol className="timeline-list">
        {entries.map((entry, i) => (
          <li className="timeline-entry" key={i}>
            <p className="timeline-when">{entry.when}</p>
            <div className="timeline-body">
              <h3 className="timeline-title">{entry.title}</h3>
              {entry.body ? <p>{entry.body}</p> : null}
            </div>
          </li>
        ))}
      </ol>
      <Buttons className="timeline-actions" buttons={buttons} />
    </Section>
  )
}

/* ── video ─────────────────────────────────────────────────────────────── */

export function readVideo(props: Record<string, unknown>): VideoView | null {
  const poster = src(props.poster_url)
  const to = external(props.video_href)
  // ADR-0043 §2 required both: a poster that plays nothing, or a link with
  // nothing to see, is not a video section. The link half still holds.
  // **A link with no poster draws since C24 item 11** — as a titled link
  // card rather than a play button over a picture. The poster *is* the player
  // (ADR-0013 §2: no script), so without one there is nothing to put a play
  // button on; there is still somewhere to send the visitor.
  if (!to) return null
  return { heading: str(props.heading), poster, alt: str(props.poster_alt), href: to, caption: str(props.caption) }
}

/** The link's name when the firm gave no words for it: the site it opens. */
function hostOf(address: string): string {
  try {
    return new URL(address).host.replace(/^www\./, '')
  } catch {
    return address
  }
}

/**
 * **A poster with a play mark that links out**, in a new tab (ADR-0043 §2).
 * No `<iframe>`: an embedded player is a third party's script on a firm's page
 * and a visitor's address handed to it — spec §9-72 asks whether that is ever
 * wanted. The poster is in a reserved 16:9 box.
 */
export function Video(view: VideoView): ReactNode {
  const name = view.alt ?? view.caption ?? view.heading ?? hostOf(view.href)
  return (
    <Section className="block video" heading={view.heading}>
      <figure className="video-figure">
        <a
          className={view.poster ? 'video-link' : 'video-link video-link-plain'}
          href={view.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {/* C24 item 11: no poster, no play button — the poster *is* the
              player here (no script), so a play button over nothing would
              promise something this section cannot do. The link stays. */}
          {view.poster ? (
            <>
              <span className="video-poster">
                <img src={view.poster} alt="" width={1280} height={720} loading="lazy" decoding="async" />
              </span>
              <span className="video-play" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
                  <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
                </svg>
              </span>
            </>
          ) : null}
          <span className="video-name">{name}</span>
        </a>
        {view.caption ? <figcaption>{view.caption}</figcaption> : null}
      </figure>
    </Section>
  )
}

/* ── before_after ──────────────────────────────────────────────────────── */

export function readBeforeAfter(props: Record<string, unknown>): BeforeAfterView | null {
  const pairs = list(props.pairs)
    .map((raw) => {
      const pair = obj(raw)
      const before = src(pair.before_url)
      const after = src(pair.after_url)
      return before && after ? { before, after, alt: str(pair.alt), caption: str(pair.caption) } : null
    })
    .filter((p): p is BeforeAfterView['pairs'][number] => p !== null)
    .slice(0, 4)
  if (pairs.length === 0) return null
  return { heading: str(props.heading), pairs }
}

/**
 * Two pictures of the firm's, **overlaid** on a pointer device — the *before*
 * in a frame the visitor widens or narrows by its `resize` handle — and **side
 * by side** on touch, where there is no handle to drag. The markup is the same
 * for both; `LIBRARY_TWO_CSS` chooses by `(hover: hover) and (pointer: fine)`.
 * *Before* and *After* are structure, not a claim: the pictures are the firm's.
 */
export function BeforeAfter(view: BeforeAfterView): ReactNode {
  return (
    <Section className="block before-after" heading={view.heading}>
      <ul className="ba-list">
        {view.pairs.map((pair, i) => (
          <li key={i}>
            <figure className="ba-pair">
              <div className="ba-frame">
                <div className="ba-after">
                  <img src={pair.after} alt={pair.alt ? `${pair.alt}, after` : ''} width={1200} height={800} loading="lazy" decoding="async" />
                  <span className="ba-tag ba-tag-after">After</span>
                </div>
                <div className="ba-before">
                  <img src={pair.before} alt={pair.alt ? `${pair.alt}, before` : ''} width={1200} height={800} loading="lazy" decoding="async" />
                  <span className="ba-tag ba-tag-before">Before</span>
                </div>
              </div>
              {pair.caption ? <figcaption>{pair.caption}</figcaption> : null}
            </figure>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ── marquee ───────────────────────────────────────────────────────────── */

export function readMarquee(props: Record<string, unknown>): MarqueeView | null {
  const items = list(props.items)
    .map((raw) => str(obj(raw).text) ?? str(raw))
    .filter((t): t is string => t !== null)
    .slice(0, 12)
  // **One to twelve since C24 item 11** (ADR-0049 §1). It was three to
  // twelve, and what that threshold was really protecting against was a band
  // of two words scrolling past for ever — so below three it is drawn
  // **static** instead of refused. A firm that wrote two words gets its two
  // words; a section it filled in is a section it sees (the owner: *"if we
  // have template section and it have valid input it should be visible in
  // website"*).
  if (items.length === 0) return null
  // Fewer than three words is a static strip, not a loop (C24 item 11).
  return { items, speed: props.speed === 'slow' ? 'slow' : 'normal', still: items.length < 3 }
}

/**
 * A band of the firm's words that moves right to left. **The second copy is
 * `aria-hidden`**, so a screen reader reads the words once; the first copy's
 * list takes focus, so a keyboard can pause the band as a pointer does by
 * hovering. Still under reduced motion, where the first copy rests at the start.
 * No heading: the band is its own statement, as `statement` is.
 */
export function Marquee(view: MarqueeView): ReactNode {
  const words = (hidden: boolean) => (
    <ul className="marquee-group" {...(hidden ? { 'aria-hidden': true } : { tabIndex: 0 })}>
      {view.items.map((item, i) => (
        <li className="marquee-item" key={i}>
          {item}
        </li>
      ))}
    </ul>
  )
  // **The static arm** (C24 item 11): one copy of the words, and no second
  // `aria-hidden` group — the duplicate exists only to make the loop seamless,
  // and a strip that does not move has nothing to make seamless.
  if (view.still) {
    return (
      <section className="block marquee marquee-still">
        <div className="marquee-viewport">{words(false)}</div>
      </section>
    )
  }
  return (
    <section className={`block marquee marquee-${view.speed}`}>
      <div className="marquee-viewport">
        <div className="marquee-track">
          {words(false)}
          {words(true)}
        </div>
      </div>
    </section>
  )
}

/* ── comparison ────────────────────────────────────────────────────────── */

export function readComparison(props: Record<string, unknown>, ctx: BlockContext): ComparisonView | null {
  const columns = list(props.columns)
    .map((raw) => str(obj(raw).label) ?? str(raw))
    .filter((c): c is string => c !== null)
    .slice(0, 4)
  // **One column since C24 item 11.** A comparison of one thing is not a
  // comparison, which is true — and it is a *list*, which the renderer draws
  // as one (`single` below). Refusing it left a firm's filled-in section
  // invisible with nothing said.
  if (columns.length === 0) return null
  const rows = list(props.rows)
    .map((raw) => {
      const row = obj(raw)
      const label = str(row.label)
      if (!label) return null
      const given = list(row.cells).map((c) => str(obj(c).text) ?? str(c))
      // One cell per column, padded or cut — a row never shifts under the wrong header.
      const cells = columns.map((_, j) => given[j] ?? null)
      return { label, cells }
    })
    .filter((r): r is ComparisonView['rows'][number] => r !== null)
    .slice(0, 15)
  if (rows.length === 0) return null
  return { heading: str(props.heading), columns, rows, buttons: readButtons(props.buttons, ctx) }
}

/**
 * **A real `<table>`**, with a header row and a header cell per row, in a
 * frame that scrolls sideways on a phone rather than squeezing four columns
 * into 390 pixels. The frame takes focus so a keyboard can scroll it, and is
 * named by the heading. Every cell is the firm's text — no tick or cross
 * stands for a claim.
 */
export function Comparison(view: ComparisonView): ReactNode {
  const { columns, rows, buttons } = view
  const label = view.heading ? { role: 'region', 'aria-label': view.heading } : {}
  return (
    <Section className={`block comparison comparison-${columns.length}`} heading={view.heading}>
      <div className="comparison-scroll" tabIndex={0} {...label}>
        <table className="comparison-table">
          <thead>
            <tr>
              <td className="comparison-corner" />
              {columns.map((c, j) => (
                <th scope="col" key={j}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <th scope="row">{row.label}</th>
                {row.cells.map((cell, j) => (
                  <td key={j}>{cell ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Buttons className="comparison-actions" buttons={buttons} />
    </Section>
  )
}

/* ── hours ─────────────────────────────────────────────────────────────── */

const WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
const DAY_NAMES: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
}
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

/** India's offset from UTC, in minutes — IST keeps no daylight saving. */
const IST_MINUTES = 5 * 60 + 30

/**
 * The weekday **in IST** at `now`, as `mon`…`sun` — spec §9-62's lesson: a
 * firm's *today* decided in UTC is wrong between 00:00 and 05:30 every night.
 * Arithmetic on the instant and a fixed offset, never the server's zone.
 */
export function istWeekday(now: Date): (typeof WEEK)[number] {
  const ist = new Date(now.getTime() + IST_MINUTES * 60 * 1000)
  return WEEK[(ist.getUTCDay() + 6) % 7] as (typeof WEEK)[number]
}

function slot(value: unknown): { open: string; close: string } | null {
  const s = obj(value)
  const open = str(s.open)
  const close = str(s.close)
  return open && close && HHMM.test(open) && HHMM.test(close) ? { open, close } : null
}

/**
 * **Read from *Your firm*'s `working_hours` at render** (ADR-0043 §2), never
 * typed twice. The service resolves the week into `days` — each with its
 * label, its times and whether it is today, decided in IST — and that is what
 * this reads first. A payload that carries the stored week instead
 * (`working_hours: {days: {mon: {open, close} | null, …}, note}`, I-1 of Wave
 * 12) is resolved here the same way, from `ctx.now`.
 *
 * **No hours set is no section**: a week in which no day has a time is `null`.
 */
export function readHours(props: Record<string, unknown>, ctx: BlockContext): HoursView | null {
  let days: HoursDay[] = list(props.days)
    .map((raw): HoursDay | null => {
      const day = obj(raw)
      const key = str(day.day)?.toLowerCase().slice(0, 3)
      const label = str(day.label) ?? (key ? (DAY_NAMES[key] ?? null) : null)
      if (!label) return null
      const times = slot(day)
      return { label, open: times?.open ?? null, close: times?.close ?? null, today: day.today === true }
    })
    .filter((d): d is HoursDay => d !== null)
    .slice(0, 7)
  const stored = obj(props.working_hours)
  if (days.length === 0 && Object.keys(obj(stored.days)).length > 0) {
    const week = obj(stored.days)
    const today = istWeekday(ctx.now ?? new Date())
    days = WEEK.map((key) => {
      const times = slot(week[key])
      return { label: DAY_NAMES[key] as string, open: times?.open ?? null, close: times?.close ?? null, today: key === today }
    })
  }
  // **C24 item 11 looked at this and left it alone.** The plan listed `hours`
  // among the sections to relax; reading the renderer showed there was
  // nothing to relax. A day with no times draws as *Closed*, which is a fact
  // a visitor wants and not a gap, so a firm that fills three days gets three
  // open days and four closed ones — which is its week. The one refusal left
  // is a week with no times at all, and that is ADR-0049 §1's *nothing at
  // all*: there is no arrangement under which it says something.
  if (!days.some((d) => d.open && d.close)) return null
  // The section's own note, and *Your firm*'s note on its week (`hours_note`,
  // 21A's resolver) — both the firm's words, so both are drawn.
  const notes = [str(props.note), str(props.hours_note) ?? str(stored.note)].filter((n): n is string => n !== null)
  return { heading: str(props.heading), note: notes.length > 0 ? notes.join(' · ') : null, days }
}

/**
 * The week, a row a day, **today emphasised** and marked `aria-current="date"`.
 * A closed day says so — the firm's week has no time for it, which is a fact
 * it entered, not one this section adds.
 */
export function Hours(view: HoursView): ReactNode {
  return (
    <Section className="block hours" heading={view.heading}>
      <dl className="hours-list">
        {view.days.map((day, i) => (
          <div
            className={`hours-day${day.today ? ' hours-today' : ''}${day.open ? '' : ' hours-closed'}`}
            key={i}
            {...(day.today ? { 'aria-current': 'date' as const } : {})}
          >
            <dt>{day.label}</dt>
            <dd>
              {day.open && day.close ? (
                <>
                  <time>{day.open}</time>–<time>{day.close}</time>
                </>
              ) : (
                'Closed'
              )}
            </dd>
          </div>
        ))}
      </dl>
      {view.note ? <p className="hours-note">{view.note}</p> : null}
    </Section>
  )
}

/* ── price_list ────────────────────────────────────────────────────────── */

export function readPriceList(props: Record<string, unknown>, ctx: BlockContext): PriceListView | null {
  const groups = list(props.groups)
    .map((raw) => {
      const group = obj(raw)
      const label = str(group.label)
      if (!label) return null
      const items = list(group.items)
        .map((r): PriceListItem | null => {
          const item = obj(r)
          const name = str(item.name)
          if (!name) return null
          return { name, description: str(item.description), price: priceText(item.price_text, ctx), tag: str(item.tag) }
        })
        .filter((i): i is PriceListItem => i !== null)
        .slice(0, 20)
      return items.length > 0 ? { label, items } : null
    })
    .filter((g): g is PriceListView['groups'][number] => g !== null)
    .slice(0, 8)
  if (groups.length === 0) return null
  return { heading: str(props.heading), groups, buttons: readButtons(props.buttons, ctx) }
}

/** A menu: a name, a dotted leader and the firm's price, grouped. Takes no order. */
export function PriceList(view: PriceListView): ReactNode {
  return (
    <Section className="block price-list" heading={view.heading}>
      <div className="pl-groups">
        {view.groups.map((group, i) => (
          <div className="pl-group" key={i}>
            <h3 className="pl-group-label">{group.label}</h3>
            <ul className="pl-items">
              {group.items.map((item, j) => (
                <li className="pl-item" key={j}>
                  <p className="pl-line">
                    <span className="pl-name">{item.name}</span>
                    {item.tag ? <span className="pl-tag">{item.tag}</span> : null}
                    {item.price ? (
                      <>
                        <span className="pl-leader" aria-hidden="true" />
                        <span className="pl-price">{item.price}</span>
                      </>
                    ) : null}
                  </p>
                  {item.description ? <p className="pl-description">{item.description}</p> : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Buttons className="pl-actions" buttons={view.buttons} />
    </Section>
  )
}

/* ── courses ───────────────────────────────────────────────────────────── */

export function readCourses(props: Record<string, unknown>, ctx: BlockContext): CoursesView | null {
  const courses = list(props.courses)
    .map((raw): CourseCard | null => {
      const course = obj(raw)
      const title = str(course.title)
      if (!title) return null
      return {
        title,
        image: src(course.image_url),
        imageAlt: str(course.image_alt),
        level: str(course.level),
        duration: str(course.duration),
        summary: str(course.summary),
        price: priceText(course.price_text, ctx),
        href: link(course.href, ctx),
      }
    })
    .filter((c): c is CourseCard => c !== null)
    .slice(0, 12)
  if (courses.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), courses, buttons: readButtons(props.buttons, ctx) }
}

/** Cards whose title links out. No enrolment and no progress (ADR-0043 §2). */
export function Courses(view: CoursesView): ReactNode {
  return (
    <Section className="block courses" heading={view.heading}>
      {view.intro ? <p className="section-intro">{view.intro}</p> : null}
      <ul className="course-list">
        {view.courses.map((course, i) => (
          <li className="course" key={i}>
            {course.image ? (
              <div className="course-art">
                <img src={course.image} alt={course.imageAlt ?? ''} width={640} height={360} loading="lazy" decoding="async" />
              </div>
            ) : null}
            <div className="course-copy">
              {course.level || course.duration ? (
                <p className="course-meta">
                  {course.level ? <span>{course.level}</span> : null}
                  {course.duration ? <span>{course.duration}</span> : null}
                </p>
              ) : null}
              <h3 className="course-title">{course.href ? <a href={course.href}>{course.title}</a> : course.title}</h3>
              {course.summary ? <p className="course-summary">{course.summary}</p> : null}
              {course.price ? <p className="course-price">{course.price}</p> : null}
            </div>
          </li>
        ))}
      </ul>
      <Buttons className="courses-actions" buttons={view.buttons} />
    </Section>
  )
}

/* ── openings ──────────────────────────────────────────────────────────── */

export function readOpenings(props: Record<string, unknown>, ctx: BlockContext): OpeningsView | null {
  const roles = list(props.roles)
    .map((raw): OpeningRole | null => {
      const role = obj(raw)
      const title = str(role.title)
      if (!title) return null
      return {
        title,
        location: str(role.location),
        kind: str(role.kind),
        experience: str(role.experience),
        summary: str(role.summary),
        href: link(role.href, ctx),
      }
    })
    .filter((r): r is OpeningRole => r !== null)
    .slice(0, 20)
  if (roles.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), roles, buttons: readButtons(props.buttons, ctx) }
}

/** A list of roles whose title links out, or to a page with a `lead_form`. Stores no applicant. */
export function Openings(view: OpeningsView): ReactNode {
  return (
    <Section className="block openings" heading={view.heading}>
      {view.intro ? <p className="section-intro">{view.intro}</p> : null}
      <ul className="role-list">
        {view.roles.map((role, i) => (
          <li className="role-item" key={i}>
            <div className="role-head">
              <h3 className="role-title">{role.href ? <a href={role.href}>{role.title}</a> : role.title}</h3>
              {role.location || role.kind || role.experience ? (
                <ul className="role-facts">
                  {[role.location, role.kind, role.experience]
                    .filter((f): f is string => f !== null)
                    .map((fact, j) => (
                      <li key={j}>{fact}</li>
                    ))}
                </ul>
              ) : null}
            </div>
            {role.summary ? <p className="role-summary">{role.summary}</p> : null}
          </li>
        ))}
      </ul>
      <Buttons className="openings-actions" buttons={view.buttons} />
    </Section>
  )
}

/* ── goal ──────────────────────────────────────────────────────────────── */

export function readGoal(props: Record<string, unknown>, ctx: BlockContext): GoalView | null {
  const targetText = str(props.target_text)
  if (!targetText) return null
  const target = count(props.target)
  const raised = count(props.raised)
  // ADR-0043 §2: the bar's width is raised ÷ target, capped at 100 %, and is
  // drawn **only when both numbers are given** — never from the texts. The
  // service sends it as `ratio` (21A); it is used as served, clamped to
  // [0, 1], and computed here only for a payload that carries none.
  const served = typeof props.ratio === 'number' && Number.isFinite(props.ratio) ? props.ratio : undefined
  const ratio =
    props.ratio === null
      ? null
      : served !== undefined
        ? Math.min(Math.max(served, 0), 1)
        : target !== null && target > 0 && raised !== null
          ? Math.min(raised / target, 1)
          : null
  return {
    heading: str(props.heading),
    intro: str(props.intro),
    targetText,
    raisedText: str(props.raised_text),
    target,
    raised,
    ratio,
    href: link(props.href, ctx),
    buttons: readButtons(props.buttons, ctx),
  }
}

/**
 * The firm's two figures as it typed them — *raised* over *target* — and,
 * only with both numbers, a bar. The bar is an ARIA `meter` whose value text
 * is the firm's own two strings. **It takes no donation**: `href` and the
 * buttons go wherever the firm sends them (spec §9-2).
 */
export function Goal(view: GoalView): ReactNode {
  const { ratio, raisedText, targetText } = view
  const percent = ratio === null ? null : Math.round(ratio * 1000) / 10
  return (
    <Section className="block goal" heading={view.heading}>
      {view.intro ? <p className="section-intro">{view.intro}</p> : null}
      <p className="goal-figures">
        {raisedText ? <span className="goal-raised">{raisedText}</span> : null}
        {raisedText ? <span className="goal-of"> / </span> : null}
        {/* The firm's `href` has no words of its own, so the target it is for carries it. */}
        {view.href ? (
          <a className="goal-target" href={view.href}>
            {targetText}
          </a>
        ) : (
          <span className="goal-target">{targetText}</span>
        )}
      </p>
      {percent !== null ? (
        <div
          className="goal-bar"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={view.target ?? 0}
          aria-valuenow={Math.min(view.raised ?? 0, view.target ?? 0)}
          aria-valuetext={raisedText ? `${raisedText} / ${targetText}` : targetText}
        >
          <span className="goal-fill" style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      <Buttons className="goal-actions" buttons={view.buttons} />
    </Section>
  )
}

/* ── product_grid ──────────────────────────────────────────────────────── */

export function readProductGrid(props: Record<string, unknown>, ctx: BlockContext): ProductGridView | null {
  const products = list(props.products)
    .map((raw): ProductCard | null => {
      const product = obj(raw)
      const name = str(product.name)
      const image = src(product.image_url)
      // Both required (ADR-0043 §2): a product grid is pictures.
      if (!name || !image) return null
      return {
        name,
        image,
        imageAlt: str(product.image_alt),
        price: priceText(product.price_text, ctx),
        badge: str(product.badge),
        href: link(product.href, ctx),
      }
    })
    .filter((p): p is ProductCard => p !== null)
    .slice(0, 24)
  if (products.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), products, buttons: readButtons(props.buttons, ctx) }
}

/** Square pictures, a name and the firm's price. A card links out; nothing is sold here. */
export function ProductGrid(view: ProductGridView): ReactNode {
  return (
    <Section className="block product-grid" heading={view.heading}>
      {view.intro ? <p className="section-intro">{view.intro}</p> : null}
      <ul className="product-list">
        {view.products.map((product, i) => (
          <li className="product" key={i}>
            <div className="product-art">
              <img src={product.image} alt={product.imageAlt ?? ''} width={600} height={600} loading="lazy" decoding="async" />
              {product.badge ? <span className="product-badge">{product.badge}</span> : null}
            </div>
            <h3 className="product-name">{product.href ? <a href={product.href}>{product.name}</a> : product.name}</h3>
            {product.price ? <p className="product-price">{product.price}</p> : null}
          </li>
        ))}
      </ul>
      <Buttons className="product-actions" buttons={view.buttons} />
    </Section>
  )
}
